import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
    Mail,
    MailOpen,
    Phone,
    Trash2,
    ChevronDown,
    ChevronRight,
} from "lucide-react";

function formatDate(d) {
    try {
        const dt = typeof d === "string" ? new Date(d) : d;
        return dt.toLocaleString("it-IT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return String(d);
    }
}

export default function AdminMessages() {
    const [messages, setMessages] = useState(null);
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get("/admin/contact-messages");
            setMessages(data);
        } catch (err) {
            toast.error(formatApiError(err, "Impossibile caricare i messaggi"));
            setMessages([]);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare definitivamente questo messaggio?")) return;
        try {
            await api.delete(`/admin/contact-messages/${id}`);
            setMessages((curr) => curr.filter((m) => m.id !== id));
            toast.success("Messaggio eliminato");
        } catch (err) {
            toast.error(formatApiError(err, "Eliminazione fallita"));
        }
    };

    const setRead = async (id, letto) => {
        // optimistic update
        setMessages((curr) =>
            curr.map((m) => (m.id === id ? { ...m, letto } : m))
        );
        try {
            await api.patch(`/admin/contact-messages/${id}/read`, null, {
                params: { letto },
            });
        } catch (err) {
            // revert on failure
            setMessages((curr) =>
                curr.map((m) => (m.id === id ? { ...m, letto: !letto } : m))
            );
            toast.error(formatApiError(err, "Operazione non riuscita"));
        }
    };

    const toggle = (id) => {
        const opening = !expanded[id];
        setExpanded((e) => ({ ...e, [id]: !e[id] }));
        // auto-mark as read the first time a message is opened
        if (opening) {
            const msg = messages?.find((m) => m.id === id);
            if (msg && !msg.letto) setRead(id, true);
        }
    };

    const unreadCount = messages?.filter((m) => !m.letto).length ?? 0;

    return (
        <div data-testid="admin-messages" className="p-10 lg:p-14">
            <header className="mb-12">
                <p className="text-xs uppercase tracking-widest text-brass">
                    Messaggi
                </p>
                <h1 className="font-serif text-4xl text-cream font-light mt-3">
                    {messages?.length ?? "—"} richieste ricevute
                    {unreadCount > 0 && (
                        <span className="ml-3 align-middle text-sm uppercase tracking-widest text-brass">
                            · {unreadCount} da leggere
                        </span>
                    )}
                </h1>
                <p className="text-sm text-cream/50 mt-2">
                    I messaggi sono ordinati dal più recente. Vengono inviati anche per email a info@dogaparquet.com.
                </p>
            </header>

            {messages === null ? (
                <p className="text-cream/40">Caricamento…</p>
            ) : messages.length === 0 ? (
                <div className="border border-dashed border-white/15 p-12 text-center text-cream/60">
                    Nessun messaggio ancora ricevuto.
                </div>
            ) : (
                <ul className="border border-white/5 divide-y divide-white/5">
                    {messages.map((m) => {
                        const open = !!expanded[m.id];
                        return (
                            <li
                                key={m.id}
                                data-testid={`admin-message-${m.id}`}
                                className="hover:bg-ink-800/60 transition-colors"
                            >
                                <button
                                    type="button"
                                    onClick={() => toggle(m.id)}
                                    className="w-full text-left p-5 flex items-center gap-6"
                                    data-testid={`admin-message-toggle-${m.id}`}
                                >
                                    <span className="text-cream/40 shrink-0">
                                        {open ? (
                                            <ChevronDown size={16} />
                                        ) : (
                                            <ChevronRight size={16} />
                                        )}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-base truncate flex items-center gap-2">
                                            {!m.letto && (
                                                <span
                                                    className="w-2 h-2 rounded-full bg-brass shrink-0"
                                                    title="Non letto"
                                                />
                                            )}
                                            <span
                                                className={
                                                    m.letto
                                                        ? "text-cream/70"
                                                        : "text-cream font-medium"
                                                }
                                            >
                                                {m.nome}
                                            </span>
                                        </p>
                                        <p className="text-xs text-cream/50 truncate mt-1">
                                            {m.messaggio.slice(0, 100)}
                                            {m.messaggio.length > 100 ? "…" : ""}
                                        </p>
                                    </div>
                                    <span className="hidden md:block text-xs text-cream/40 shrink-0">
                                        {formatDate(m.created_at)}
                                    </span>
                                </button>

                                {open && (
                                    <div className="px-5 pb-6 pl-14 space-y-5">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                            <a
                                                href={`mailto:${m.email}`}
                                                className="flex items-center gap-3 text-cream/85 hover:text-brass transition-colors"
                                            >
                                                <Mail size={14} className="text-brass" />
                                                {m.email}
                                            </a>
                                            <a
                                                href={`tel:${m.telefono}`}
                                                className="flex items-center gap-3 text-cream/85 hover:text-brass transition-colors"
                                            >
                                                <Phone size={14} className="text-brass" />
                                                {m.telefono}
                                            </a>
                                        </div>
                                        <div className="border-l-2 border-brass/40 pl-5 text-cream/85 whitespace-pre-wrap leading-relaxed">
                                            {m.messaggio}
                                        </div>
                                        <div className="flex items-center justify-between pt-2">
                                            <span className="text-[11px] text-cream/40">
                                                ID: <span className="font-mono">{m.id}</span>
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setRead(m.id, !m.letto)}
                                                    data-testid={`admin-message-read-${m.id}`}
                                                    className="inline-flex items-center gap-2 border border-white/10 hover:border-brass hover:text-brass text-cream/70 px-3 py-2 text-xs uppercase tracking-widest transition-colors"
                                                >
                                                    {m.letto ? (
                                                        <>
                                                            <Mail size={14} /> Segna non letto
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MailOpen size={14} /> Segna letto
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(m.id)}
                                                    data-testid={`admin-message-delete-${m.id}`}
                                                    className="inline-flex items-center gap-2 border border-white/10 hover:border-red-400 hover:text-red-400 text-cream/70 px-3 py-2 text-xs uppercase tracking-widest transition-colors"
                                                >
                                                    <Trash2 size={14} /> Elimina
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
